import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { filter, map, Observable, Subject } from 'rxjs';
import { CreditReport } from '@app/app/interfaces/credit-reports/credit-report.model';
import { take, takeUntil } from 'rxjs/operators';
import { FinancesState } from '@app/app/store/finances/finances.state';
import { Store } from '@ngxs/store';
import { GetOwnerCreditReport } from '@app/app/store/finances/finances.actions';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '@app/app/components/dialogs/confirm-dialog/confirm-dialog.component';
import { Document } from '@app/app/interfaces/document.model';
import { DocumentsState } from '@app/app/store/documents/documents.state';
import { sortBy } from 'lodash';
import { environment } from '@app/environments/environment';
import { AuthState } from '@app/app/store/auth/auth.state';
import { DocumentsService } from '@app/app/services/documents.service';

@Component({
  selector: 'app-credit-score-display',
  templateUrl: 'credit-score-display.component.html',
  styleUrls: ['credit-score-display.component.scss'],
})
export class CreditScoreDisplayComponent implements OnChanges, OnInit, OnDestroy {

  @Input() contactId: number;

  documents$: Observable<Document[]> = this.store.select(DocumentsState.documents);
  latestDocument$: Observable<Document>;

  creditReport$: Observable<CreditReport>;

  destroyed$ = new Subject<boolean>();
  creditRangeIndex$: Observable<number>;

  creditScoresRangeIncrements = [
    {
      label: 'Poor',
      low: 300,
      high: 579,
      colorClass: 'bg-lendio-red-400'
    },
    {
      label: 'Fair',
      low: 580,
      high: 669,
      colorClass: 'bg-lendio-orange-400',
    },
    {
      label: 'Good',
      low: 670,
      high: 739,
      colorClass: 'bg-lendio-yellow-400'
    },
    {
      label: 'Very Good',
      low: 740,
      high: 799,
      colorClass: 'bg-lendio-lime-400'
    },
    {
      label: 'Exceptional',
      low: 800,
      high: 850,
      colorClass: 'bg-lendio-green-400'
    },
  ];

  constructor(
    private dialog: MatDialog,
    private store: Store,
    private doc: DocumentsService
  ) {}

  ngOnInit():void {
    this.latestDocument$ = this.documents$.pipe(
      takeUntil(this.destroyed$),
      filter(docs => !!docs.length),
      map(docs => {
        return sortBy(docs, 'created')[docs.length - 1];
      })
    );
  }

  ngOnChanges(): void {
    this.creditReport$ = this.store.select( FinancesState.creditReportByContactId(this.contactId) );
    this.creditRangeIndex$ = this.creditReport$.pipe(
      takeUntil(this.destroyed$),
      filter( creditReport => !!creditReport?.creditScore ),
      map( creditReport => {
        return this.getCreditRangeIndex(creditReport.creditScore);
      })
    )
  }

  viewReport() {
    this.latestDocument$.pipe(
      take(1),
      filter(doc => !!doc?.id),
    ).subscribe(doc => {
      const url = `${environment.apiUrl}/document/${doc.id}?stream=1`;
      this.doc.openInNewTab(url, doc.id);
    });
  }

  pullOwnerCredit() {
    const dialogRef = this.dialog.open( ConfirmDialogComponent, {
      data: {
        title: 'Are you sure you want to pull credit?',
        description: 'Hard credit inquiries usually show on credit reports and impact credit scores.',
        confirmLabel: 'Pull credit'
      }
    });

    dialogRef.componentInstance.onCancel.pipe(
      take(1),
    ).subscribe( () => dialogRef.close() );
    dialogRef.componentInstance.onConfirm.pipe(
      take(1),
    ).subscribe( () => {
      dialogRef.close();
      this.store.dispatch( new GetOwnerCreditReport(this.contactId) );
    });
  }

  getCreditRangeIndex(score) {
    return score <= 300
      ? 1
      : score >= 800
        ? 5
        : this.creditScoresRangeIncrements.reduce( (res, cur, i) => {
          return score >= cur.low && score <= cur.high
            ? i + 1
            : score > 850
              // either very exceptional or extremely poor
              ? 4 : res;
        }, 0);
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
